import { Box, Button, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import PhoneIcon from '@mui/icons-material/Phone';
import CallEndIcon from '@mui/icons-material/CallEnd';
import EloOrbit from './Orbit';
import { useTranslation } from 'react-i18next';

const Elo = () => {
    const { t, i18n } = useTranslation();
    return (
        <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column-reverse', md: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                overflow: 'hidden',
                borderRadius: '16px',
                boxShadow: '0 0 12px rgba(147, 51, 234, 0.35)',
                transition: 'all 0.3s ease',
                maxWidth: '100%',
                margin: { xs: 2, md: 6 },
                p: 1.5,
                gap: { xs: 1.5, md: 3 },
                position: 'relative',
                background: "#112240",
            }}
        >
            {/* Text section */}
            <Box sx={{ flex: 1, px: { xs: 0, md: 3 }, textAlign: { xs: 'center', md: 'left' } }}>
                <Typography mb={0} variant="h5" component="h2" gutterBottom sx={{ color: "#64FFDA", fontSize: "1.6rem" }}>
                    {i18n.language === "en" ? "Elo - Ai SDR" : "Elo – SDR IA"}
                </Typography>
                <Typography variant="subtitle1" color="#64FFDA" mb={1.5} sx={{ fontSize: "0.95rem" }}>
                    {i18n.language === "en" ? "(Sales Development Representative) - Available to business clinets" : "(Représentant du développement des ventes) - Disponible pour les clients professionnels"}
                </Typography>
                <Button
                    variant="contained"
                    href={`${i18n.language}/contact-us`}
                    sx={{
                        background: 'linear-gradient(135deg, #3b0764, #9333ea)',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        maxWidth: { xs: '100vw', md: '40vw' },
                        width: '100%',
                        margin: '0 auto',
                        paddingX: '14px',
                        paddingY: '6px',
                        fontSize: '0.85rem',
                        textTransform: 'capitalize',
                        boxShadow: '0 0 12px rgba(147, 51, 234, 0.4)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #581c87, #a855f7)',
                            boxShadow: '0 0 18px rgba(147, 51, 234, 0.55)',
                        },
                    }}
                >
                    {i18n.language === "en" ? "Hire me" : "Engagez-moi"}
                </Button>
                {/* SDR Responsibilities Section */}
                <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.5,
                        mt: 3,
                    }}
                >
                    {(i18n.language === "en"
                        ? [
                            { text: "Prospecting", emoji: "🔍" },
                            { text: "Lead qualification", emoji: "✅" },
                            { text: "Outbound outreach", emoji: "📤" },
                            { text: "Inbound follow-up", emoji: "📥" },
                            { text: "Appointment setting", emoji: "📅" },
                            { text: "Pipeline management", emoji: "📊" },
                            { text: "Nurturing prospects", emoji: "🌱" },
                            { text: "Collaboration", emoji: "🤝" },
                            { text: "Performance tracking", emoji: "📈" },
                            { text: "Continuous learning", emoji: "📚" },
                        ]
                        : [
                            { text: "Prospection", emoji: "🔍" },
                            { text: "Qualification des leads", emoji: "✅" },
                            { text: "Approche sortante", emoji: "📤" },
                            { text: "Suivi des prospects entrants", emoji: "📥" },
                            { text: "Prise de rendez-vous", emoji: "📅" },
                            { text: "Gestion du pipeline", emoji: "📊" },
                            { text: "Nurture des prospects", emoji: "🌱" },
                            { text: "Collaboration", emoji: "🤝" },
                            { text: "Suivi de performance", emoji: "📈" },
                            { text: "Apprentissage continu", emoji: "📚" },
                        ]
                    ).map((item, index) => (
                        <Box
                            key={index}
                            component={motion.div}
                            whileHover={{ scale: 1.03 }}
                            transition={{ type: "spring", stiffness: 200 }}
                            sx={{
                                p: 1.5,
                                borderRadius: "12px",
                                bgcolor: "background.paper",
                                boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Typography sx={{ fontSize: "1.1rem", lineHeight: 1 }}>
                                {item.emoji}
                            </Typography>
                            <Typography
                                variant="body2"
                                fontWeight="500"
                                color="text.primary"
                                sx={{
                                    wordBreak: "break-word",
                                    lineHeight: 1.2,
                                    fontSize: { xs: "0.8rem", sm: "0.85rem" },
                                }}
                            >
                                {item.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Video section with buttons */}
            <Box
                sx={{
                    flexShrink: 0,
                    // width: { xs: '100%', md: '35%' },
                    // height: { xs: 300, md: 350 },
                    width: { xs: '100%', md: '40%' },
                    height: { xs: 350, md: 425 },
                    mb: { xs: 1.5, md: 0 },
                    borderRadius: '16px',
                    overflow: 'hidden',
                    backgroundColor: 'black',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                }}
            >
                <EloOrbit />

                {/* iPhone style call buttons below video */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 4,
                        zIndex: 10,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        borderRadius: '20px',
                        padding: '6px 18px',
                        backdropFilter: 'blur(6px)',
                        marginBottom: 3
                    }}
                >
                    {/* Accept call */}
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            bgcolor: '#34C759',
                            borderRadius: '50%',
                            boxShadow: '0 3px 8px rgba(52, 199, 89, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                                boxShadow: '0 5px 12px rgba(52, 199, 89, 0.8)',
                                transform: 'scale(1.08)',
                            },
                            transition: 'all 0.25s ease',
                        }}
                        aria-label="Accept call"
                        role="button"
                        tabIndex={0}
                    >
                        <PhoneIcon sx={{ color: 'white', fontSize: 22 }} />
                    </Box>

                    {/* Decline call */}
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            bgcolor: '#FF3B30',
                            borderRadius: '50%',
                            boxShadow: '0 3px 8px rgba(255, 59, 48, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                                boxShadow: '0 5px 12px rgba(255, 59, 48, 0.8)',
                                transform: 'scale(1.08)',
                            },
                            transition: 'all 0.25s ease',
                        }}
                        aria-label="Decline call"
                        role="button"
                        tabIndex={0}
                    >
                        <CallEndIcon sx={{ color: 'white', fontSize: 22 }} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Elo;
